import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import { FormlyValueChangeEvent } from '@ngx-formly/core/lib/models';
import {
  debounceTime,
  filter,
  merge,
  startWith,
  Subject,
  Subscription,
  takeUntil,
} from 'rxjs';
import {
  ICascadingConfig,
  ICascadingPrefillConfig,
} from '../../../models/irembo-cascading-config';
import { getValueFromNestedObject } from '../../../../utils/utils/nested-object-extractor.util';
import { findFormControl } from '../../../../utils/utils/data-fetch-widget-utils';
import { IEnvironment } from '../../../models/environment.model';
import { ToastService } from '../../../services/toast/toast.service';
import { TranslateService } from '@ngx-translate/core';
import {
  FormField,
  IConditionalLevelSettings,
  IConditionResult,
} from '../../../models/location-widget.model';
import { alphaNanoIdUtil } from '../../../../utils/utils/alpha-nano-id.util';

interface IHierarchyResponse {
  code: string;
  data: {
    hierarchy: Record<string, IDatasetItemResponse>;
    targetItem: IDatasetItemResponse;
    searchBy: string;
    searchValue: string;
    found: boolean;
  };
}

interface IDatasetItemResponse {
  id: string;
  datasetId: string;
  label: string;
  value: string;
  details: Record<string, unknown>;
}

@Component({
  standalone: false,
  selector: 'irembogov-custom-cascading-drop-downs',
  templateUrl: './custom-cascading-drop-downs.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CustomCascadingDropDownsComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, OnInit, AfterViewInit
{
  private environment: IEnvironment;
  private subscriptions = new Subscription();
  configs: ICascadingConfig[] = [];
  items: Record<string, Record<string, unknown>[] | null> = {};
  loading: Record<string, boolean> = {};
  subscribeSubject: Subject<void> = new Subject();

  subTypes = ['CUSTOM_DROPDOWN_DATASET', 'CUSTOM_DROPDOWN_DATASET_PAGINATED'];
  locationLevels: Record<string, string> = {
    PROVINCE: 'Province',
    DISTRICT: 'District',
    SECTOR: 'Sector',
    CELL: 'Cell',
    VILLAGE: 'Village',
  };
  dropdownFieldTypes = ['customdropdown', 'customdropdownpaginated'];

  /** Track last prefilled value to avoid redundant API calls */
  private lastPrefillValue: string | null = null;
  /** Flag to indicate prefill is in progress */
  private isPrefilling = false;

  /**
   * Get configs with updated expressions for change detection
   */
  get processedConfigs(): ICascadingConfig[] {
    // Return a new array reference to trigger change detection
    return [...this.configs];
  }

  constructor(
    @Inject('environment') environment: IEnvironment,
    private readonly http: HttpClient,
    private readonly cd: ChangeDetectorRef,
    private readonly toastService: ToastService,
    private readonly translateService: TranslateService
  ) {
    super();
    this.environment = environment;
  }

  ngOnInit(): void {
    // Create a copy to ensure change detection works properly
    this.configs = [...(this.field.props['configs'] || [])];

    this.cascadeRequiredValidation();

    this.watchForConfigChanges();
  }

  ngAfterViewInit(): void {
    // Setup prefill functionality if configured
    this.setupPrefill();

    let initialLevels: IConditionResult = {
      locationFirstLevel: this.field.props['locationFirstLevel'],
      locationLastLevel: this.field.props['locationLastLevel'],
    };
    const fieldChanges$ = this.field.parent?.parent?.options?.fieldChanges;
    if (fieldChanges$) {
      const debouncedValueChanges$ = fieldChanges$.pipe(
        filter(change => change.type === 'valueChanges'),
        debounceTime(400)
      );
      const otherChanges$ = fieldChanges$.pipe(
        filter(change => change.type !== 'valueChanges')
      );
      this.subscriptions.add(
        merge(debouncedValueChanges$, otherChanges$).subscribe({
          next: () => {
            const conditionalSettings =
              this.field.props['conditionalLevelSettings'];
            if (conditionalSettings) {
              const newLevels = this.evaluateConditions(
                conditionalSettings,
                this.field,
                this.field.parent?.parent?.form?.value
              );
              const levelsHaveChanged = !this.compareConditionResults(
                initialLevels,
                newLevels
              );
              if (this.field.props && levelsHaveChanged) {
                const newConfigs = this.generateDatasetConfigs(
                  newLevels.locationFirstLevel,
                  newLevels.locationLastLevel,
                  this.field.props?.label ?? 'Location',
                  this.field.props.required ?? false
                );
                this.field.props['configs'] = newConfigs;
                initialLevels = newLevels;
                this.configs = [...newConfigs];
                this.cd.detectChanges();
              }
            }
          },
        })
      );
    }
  }

  /**
   * Observes changes made through expressions in the `configs` of the Formly input field
   * and triggers change detection if such changes are detected.
   */
  watchForConfigChanges(): void {
    // detect changes made through expressions in the `configs` of the formly input
    this.field.options?.fieldChanges
      ?.pipe(
        filter(
          (e: FormlyValueChangeEvent) =>
            e.field.key === this.field.key && e.type === 'expressionChanges'
        ),
        takeUntil(this.subscribeSubject)
      )
      .subscribe(data => {
        // check if changes made affected the `configs` property of the field
        const configsPattern = /props\.configs\[(\d+)\]/;
        const match = data?.['property']?.match(configsPattern);
        if (match) {
          // use array spreading to allow change detection
          this.configs = [...this.configs];
          this.cd.detectChanges();
        }

        if (data?.['property'] === 'props.configs') {
          this.configs = data.value;
          this.cd.detectChanges();
        }

        if (data?.['property'] === 'props.required') {
          this.cascadeRequiredValidation();
          this.cd.detectChanges();
        }
      });

    // Also watch for model changes that might affect expressions in child configs
    this.field.options?.fieldChanges
      ?.pipe(
        filter((e: FormlyValueChangeEvent) => e.type === 'valueChanges'),
        takeUntil(this.subscribeSubject)
      )
      .subscribe(() => {
        // Trigger change detection to re-evaluate expressions
        this.cd.detectChanges();
      });
  }

  handleActiveDropDown(event: ICascadingConfig) {
    this.initializeDropDown(event);
  }

  initializeDropDown(activeControl: ICascadingConfig) {
    if (!activeControl) return;
    this.loading[activeControl.key] = true;
    let searchId: unknown = activeControl.value;
    if (
      typeof activeControl.value === 'object' &&
      activeControl.parentBindKey
    ) {
      // Handle parent values for both single and multi-select
      if (Array.isArray(activeControl.value)) {
        // For multi-select parent, use the first selected value for now
        searchId =
          activeControl.value.length > 0
            ? activeControl.value[0][activeControl.parentBindKey]
            : null;
      } else {
        searchId = activeControl.value[activeControl.parentBindKey];
      }
    }

    // Handle any remaining array cases that might have passed through
    if (Array.isArray(searchId)) {
      searchId = searchId.length > 0 ? searchId[0] : null;
    }
    let url = searchId
      ? `${activeControl.dataset.url}/${searchId}`
      : activeControl.dataset.url;

    if (activeControl.dataset.useBaseUrl) {
      url = this.prependUrlWithApiGatewayBaseUrl(url);
    }

    const excludedItems: Array<string | boolean | number> | undefined =
      activeControl.excludedItems;
    const excludedItemKey =
      activeControl.excludedItemKey ?? activeControl.bindlabel ?? 'label';

    this.subscriptions.add(
      this.http.get<Record<string, unknown> | []>(url).subscribe({
        next: res => {
          const datasetResponse = Array.isArray(res)
            ? res
            : getValueFromNestedObject(res, activeControl.dataset.dataField);

          let data: Record<string, unknown>[] = [];
          if (excludedItems && Array.isArray(excludedItems)) {
            data = datasetResponse.filter(
              (dataItem: Record<string, unknown>) => {
                return !excludedItems.includes(
                  <string | boolean | number>dataItem[excludedItemKey]
                );
              }
            );
          } else {
            data = [...datasetResponse];
          }

          data.forEach((dataItem: Record<string, unknown>) => {
            if (!dataItem[activeControl.bindlabel]) {
              return;
            }
            dataItem[activeControl.bindlabel] = this.translateService.instant(
              <string>dataItem[activeControl.bindlabel]
            );
          });
          this.items = { ...this.items, [activeControl.key]: data };
          this.loading = { ...this.loading, [activeControl.key]: false };
          this.cd.detectChanges();
        },
        error: () => {
          this.loading = { ...this.loading, [activeControl.key]: false };
          this.toastService.show({
            body: `Error loading ${activeControl.label}`,
            type: 'error',
          });
          this.cd.detectChanges();
        },
      })
    );
  }

  private prependUrlWithApiGatewayBaseUrl(url: string): string {
    const apiGatewayBaseUrl: string | undefined =
      this.environment.apiGatewayBaseUrl;
    if (!apiGatewayBaseUrl) {
      throw new Error(
        'useBaseUrl: main portal environment ApiGatewayBaseUrl property is required'
      );
    } else {
      url = `${apiGatewayBaseUrl}${url}`;
    }

    return url;
  }

  private evaluateConditions(
    configuration: IConditionalLevelSettings,
    field: any,
    model: any
  ): IConditionResult {
    if (!configuration?.configurationList) {
      return {
        locationFirstLevel: this.field.props['locationFirstLevel'],
        locationLastLevel: this.field.props['locationLastLevel'],
      };
    }

    for (const config of configuration.configurationList) {
      let conditionMet = true;
      let previousConditionResult = true;
      let currentOperator: string | null = null;

      for (const condition of config.conditions) {
        const {
          formField,
          formFieldProperty,
          comparator,
          fieldvalue,
          formStateValue,
          operator,
          valueAsNumber,
        } = condition;

        if (operator) {
          currentOperator = operator;
        }

        let currentConditionResult = false;

        if (formField.value === 'formState') {
          let pathValue = formStateValue?.formStateKey
            ? this.field.options?.formState[formStateValue?.formStateKey]
            : null;
          if (formStateValue?.fieldKey) {
            pathValue = pathValue?.[formStateValue?.fieldKey];
          }

          if (formStateValue?.valuePath) {
            pathValue = pathValue?.[formStateValue.valuePath];
          }

          let valueToCompare = fieldvalue;
          if (valueAsNumber) {
            valueToCompare = Number(valueToCompare);
          } else {
            valueToCompare = this.formatComparisonValue(fieldvalue, formField);
          }

          currentConditionResult = this.compareValues(
            pathValue,
            valueToCompare,
            comparator
          );
        } else {
          let { fieldRefPath } = formField;
          const rootModel = model;

          if (
            formField.fieldSubType &&
            this.subTypes.includes(formField.fieldSubType)
          ) {
            fieldRefPath = `${fieldRefPath}?.value`;
          }

          let fieldValue = rootModel;
          if (fieldRefPath) {
            const pathParts = fieldRefPath.split('?.');
            for (const part of pathParts) {
              fieldValue = fieldValue?.[part];
            }
          }

          const valueToCompare = this.formatComparisonValue(
            fieldvalue,
            formField
          );

          if (formFieldProperty?.type !== 'boolean') {
            currentConditionResult = this.compareValues(
              fieldValue,
              valueToCompare,
              comparator
            );
          } else {
            if (formFieldProperty?.value === 'validity') {
              const parts = fieldRefPath.split('?.').join('.');
              const parentForm = field?.parent?.parent?.form;
              const fieldToCheckValidity = parentForm.get(parts);
              const validity = fieldvalue ? 'valid' : 'invalid';
              currentConditionResult =
                fieldToCheckValidity?.[validity] === true;
            } else {
              const parts = fieldRefPath.split('?.');
              let chain = field?.parent?.parent?.parent;
              for (const part of parts) {
                chain = chain?.fieldGroup?.find((f: any) => f.key === part);
              }
              const propValue = chain?.props?.[formFieldProperty?.value];
              currentConditionResult = propValue === fieldvalue;
            }
          }
        }
        if (currentOperator) {
          if (currentOperator === 'AND') {
            conditionMet = previousConditionResult && currentConditionResult;
          } else if (currentOperator === 'OR') {
            conditionMet = previousConditionResult || currentConditionResult;
          }
          currentOperator = null;
        } else {
          conditionMet = currentConditionResult;
        }

        previousConditionResult = currentConditionResult;

        if (config.conditions.length > 1) {
          if (currentOperator === 'AND' && !conditionMet) {
            break;
          }
          if (currentOperator === 'OR' && conditionMet) {
            break;
          }
        }
      }

      if (conditionMet) {
        return {
          locationFirstLevel: config.locationFirstLevel,
          locationLastLevel: config.locationLastLevel,
        };
      }
    }

    return {
      locationFirstLevel: this.field.props['locationFirstLevel'],
      locationLastLevel: this.field.props['locationLastLevel'],
    };
  }

  private formatComparisonValue(value: any, field: FormField): string {
    if (typeof value === 'object') {
      if (this.dropdownFieldTypes.includes(field.fieldType)) {
        if (field.fieldSubType && this.subTypes.includes(field.fieldSubType)) {
          return value.value;
        }
      }

      return JSON.stringify(value);
    }
    return value;
  }

  private compareValues(
    current: any,
    expected: any,
    comparator = 'EQ'
  ): boolean {
    switch (comparator) {
      case 'EQ':
        return current == expected;
      case 'NEQ':
        return current != expected;
      case 'GT':
        return current > expected;
      case 'LT':
        return current < expected;
      case 'GTE':
        return current >= expected;
      case 'LTE':
        return current <= expected;
      case 'CONTAINS':
        return typeof current === 'string' && current.includes(expected);
      default:
        return current == expected;
    }
  }

  private generateDatasetConfigs = (
    firstLevel: string,
    lastLevel: string | undefined,
    labelPrefix: string,
    required: boolean
  ): ICascadingConfig[] => {
    const datasetOptions: string[] = Object.keys(this.locationLevels);
    const firstLevelIndex: number | undefined =
      datasetOptions.indexOf(firstLevel);

    const randomKey: string = alphaNanoIdUtil(5);

    const datasetConfigs: ICascadingConfig[] = [];
    const datasetFieldKeys: Record<string, string> = {};

    datasetOptions.every((option: string, i: number) => {
      datasetFieldKeys[option] = `${option}`;
      if (i < firstLevelIndex) {
        return true;
      }

      if (
        option.toLowerCase() === firstLevel?.toLowerCase() ||
        option.toLowerCase() ===
          Object.keys(this.locationLevels)[0].toLowerCase()
      ) {
        datasetConfigs.push(
          this.createDataset(
            `${datasetFieldKeys[option]}${randomKey}`,
            `${labelPrefix} ${this.locationLevels[option]}`,
            option,
            required
          )
        );
        return true;
      }

      datasetConfigs.push(
        this.createDataset(
          `${datasetFieldKeys[option]}${randomKey}`,
          `${labelPrefix} ${this.locationLevels[option]}`,
          null,
          required,
          `${datasetFieldKeys[datasetOptions[i - 1]]}${randomKey}`
        )
      );

      if (option.toLowerCase() === lastLevel?.toLowerCase()) {
        return false;
      }
      return true;
    });

    return datasetConfigs;
  };

  private createDataset(
    key: string,
    label: string,
    datasetCode: string | null,
    required: boolean,
    parent: string | null = null
  ): ICascadingConfig {
    const url = datasetCode
      ? `/admin/v1/dataset-items/by-dataset-code/${datasetCode}`
      : '/admin/v1/dataset-items/by-details';
    const dataset: ICascadingConfig = {
      key,
      label,
      parent,
      dataset: {
        url,
        dataField: 'data',
        useBaseUrl: true,
      },
      required,
      bindlabel: 'label',
      bindvalue: '',
      placeholder: `Select ${label}`,
    };

    if (!datasetCode) {
      dataset['parentBindKey'] = 'id';
    }

    return dataset;
  }

  private compareConditionResults(
    initialLevels: IConditionResult,
    newLevels: IConditionResult
  ): boolean {
    if (initialLevels.locationFirstLevel !== newLevels.locationFirstLevel) {
      return false;
    }

    if (initialLevels.locationLastLevel !== newLevels.locationLastLevel) {
      return false;
    }

    return true;
  }

  private cascadeRequiredValidation() {
    const updatedConfigs = this.configs.map(config => ({
      ...config,
      required: this.field.props.required ?? false,
    }));
    this.configs = updatedConfigs;
  }

  /**
   * Setup prefill functionality to watch for value changes and auto-populate dropdowns.
   * Only activates when prefill.enabled is true AND prefill.valueFrom is specified.
   * Otherwise, the component works exactly as before.
   */
  private setupPrefill(): void {
    const prefillConfig = this.field.props['prefill'] as
      | ICascadingPrefillConfig
      | undefined;

    // Only setup prefill if both enabled and valueFrom are specified
    if (!prefillConfig?.enabled || !prefillConfig?.valueFrom) {
      return;
    }

    // Watch for value changes in the form, starting with null to trigger initial check
    this.field.options?.fieldChanges
      ?.pipe(
        filter((e: FormlyValueChangeEvent) => e.type === 'valueChanges'),
        startWith(null),
        debounceTime(300),
        takeUntil(this.subscribeSubject)
      )
      .subscribe(() => {
        const value = this.getValueFromPath(
          prefillConfig.valueFrom,
          prefillConfig.valueFromRoot
        );

        // Only trigger prefill if value exists, changed, and not currently prefilling
        if (value && value !== this.lastPrefillValue && !this.isPrefilling) {
          this.executePrefill(value, prefillConfig);
        }
      });
  }

  /**
   * Get value from a field key or path
   */
  private getValueFromPath(
    keyOrPath: string,
    valueFromRoot?: boolean
  ): string | null {
    if (!keyOrPath) return null;

    if (valueFromRoot) {
      let rootForm = this.form;
      while (rootForm?.parent) {
        rootForm = rootForm.parent;
      }
      const rootModel = rootForm?.value;

      const parts = keyOrPath.split('.');
      let value: unknown = rootModel;

      for (const part of parts) {
        value = (value as Record<string, unknown>)?.[part];
      }

      return value as string | null;
    }

    const control = findFormControl(this.form, keyOrPath);
    return control?.value as string | null;
  }

  /**
   * Execute prefill by fetching hierarchy and applying to dropdowns
   */
  private executePrefill(
    searchValue: string,
    config: ICascadingPrefillConfig
  ): void {
    this.isPrefilling = true;
    this.lastPrefillValue = searchValue;

    const datasetCode =
      config.datasetCode || this.extractDatasetCodeFromConfigs();
    const searchBy = config.searchBy || 'value';

    const url = `${
      this.environment.apiGatewayBaseUrl
    }/admin/v1/dataset-items/by-dataset-code/${datasetCode}/resolve-hierarchy?searchValue=${encodeURIComponent(
      searchValue
    )}&searchBy=${searchBy}`;

    this.subscriptions.add(
      this.http.get<IHierarchyResponse>(url).subscribe({
        next: res => {
          if (res.data?.found && res.data?.hierarchy) {
            this.applyHierarchyToDropdowns(res.data.hierarchy);
          }
          this.isPrefilling = false;
        },
        error: () => {
          this.isPrefilling = false;
        },
      })
    );
  }

  /**
   * Extract dataset code from the last config's URL or use the location level
   */
  private extractDatasetCodeFromConfigs(): string {
    // Try to find a config with dataset code in URL
    for (let i = this.configs.length - 1; i >= 0; i--) {
      const config = this.configs[i];
      const match = config.dataset?.url?.match(/by-dataset-code\/([A-Z_]+)/);
      if (match) {
        return match[1];
      }
    }

    // Fallback to locationLastLevel or VILLAGE
    return this.field.props['locationLastLevel'] || 'VILLAGE';
  }

  /**
   * Apply the hierarchy response to the cascading dropdowns
   * Maps API response keys (PROVINCE, DISTRICT...) to config keys (COMPANY_PROVINCE...)
   *
   * For the first level: only set the VALUE (don't overwrite items, they're loaded from API)
   * For child levels: set both items and value
   */
  private applyHierarchyToDropdowns(
    hierarchy: Record<string, IDatasetItemResponse>
  ): void {
    const hierarchyEntries = Object.entries(hierarchy);
    const locationLevelKeys = Object.keys(this.locationLevels);

    // Get the configured first and last levels
    const locationFirstLevel = this.field.props['locationFirstLevel'] as string;
    const locationLastLevel = this.field.props['locationLastLevel'] as string;

    // Build mapping from dataset codes to config keys
    const datasetToConfigKey: Record<string, string> = {};
    const configKeyToIndex: Record<string, number> = {};

    this.configs.forEach((config, index) => {
      configKeyToIndex[config.key] = index;
      // Try to extract dataset code from URL
      const match = config.dataset?.url?.match(/by-dataset-code\/([A-Z_]+)/);
      if (match) {
        datasetToConfigKey[match[1]] = config.key;
      }
    });

    // For configs without dataset code in URL, map by position relative to location levels
    if (Object.keys(datasetToConfigKey).length < this.configs.length) {
      let firstLevelIndex = -1;
      for (let i = 0; i < this.configs.length; i++) {
        const match = this.configs[i].dataset?.url?.match(
          /by-dataset-code\/([A-Z_]+)/
        );
        if (match) {
          firstLevelIndex = locationLevelKeys.indexOf(match[1]);
          break;
        }
      }

      if (firstLevelIndex >= 0) {
        this.configs.forEach((config, idx) => {
          const levelKey = locationLevelKeys[firstLevelIndex + idx];
          if (levelKey && !datasetToConfigKey[levelKey]) {
            datasetToConfigKey[levelKey] = config.key;
          }
        });
      }
    }

    // Identify the first config key (the one that loads items from API directly)
    const firstConfigKey = this.configs[0]?.key;

    // Apply hierarchy values
    const newItems: Record<string, Record<string, unknown>[]> = {};
    const newValue: Record<string, Record<string, unknown>> = {};

    // Filter hierarchy entries to only include levels between firstLevel and lastLevel
    const firstLevelIdx = locationFirstLevel
      ? locationLevelKeys.indexOf(locationFirstLevel)
      : 0;
    const lastLevelIdx = locationLastLevel
      ? locationLevelKeys.indexOf(locationLastLevel)
      : locationLevelKeys.length - 1;

    hierarchyEntries.forEach(([datasetCode, item]) => {
      const configKey = datasetToConfigKey[datasetCode];
      const levelIdx = locationLevelKeys.indexOf(datasetCode);

      // Skip levels outside the configured range
      if (levelIdx < firstLevelIdx || levelIdx > lastLevelIdx) {
        return;
      }

      if (configKey) {
        // Translate the label
        const translatedItem = {
          ...item,
          label: this.translateService.instant(item.label),
        };

        // Always set the value
        newValue[configKey] = translatedItem;

        // For the FIRST level: don't overwrite items (they're loaded from API)
        // For other levels: set items (since they depend on parent selection)
        if (configKey !== firstConfigKey) {
          newItems[configKey] = [translatedItem];
        }
      }
    });

    // Update items for child levels only (preserve first level's API-loaded items)
    this.items = { ...this.items, ...newItems };

    // Set the form value
    this.formControl.setValue(newValue);
    this.formControl.markAsTouched();
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.subscribeSubject.unsubscribe();
  }
}
